<template>
  <button
    class="base-tag"
    :class="classes"
    :style="tagInlineStyle"
    type="button"
    @click="handleClick"
  >
    <slot name="prepend-icon" />
    <slot />
    <slot name="append-icon" />
  </button>
</template>

<script setup lang="ts">
interface Props {
  variant?: 'secondary' | 'primary' | 'light' | 'dark' | 'transparent'
  outlined?: boolean
  rounded?: boolean
  size?: 'mini' | 'md'
  color?: string
  gap?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'secondary',
  size: 'md',
})
const emit = defineEmits<{
  (e: 'click', val: Event): void
}>()

const classes = computed(() => {
  const hasCustomColor = Boolean(props.color)
  return {
    [`base-tag--size-${props.size}`]: true,
    [`base-tag--variant-${props.variant}`]: !hasCustomColor,
    'base-tag--custom-planner-color': hasCustomColor,
    'base-tag--outlined': props.outlined && !hasCustomColor,
    'base-tag--rounded': props.rounded,
  }
})

const tagInlineStyle = computed((): Record<string, string> | undefined => {
  const style: Record<string, string> = {}
  if (props.color) {
    style['--tag-planner-bg'] = props.color
  }
  if (props.gap != null && props.gap !== '') {
    style['--tag-gap'] = props.gap
  }
  return Object.keys(style).length > 0 ? style : undefined
})

function handleClick(event: Event) {
  emit('click', event)
}
</script>

<style lang="scss" scoped>
.base-tag {
  @include drop-button-styles;

  display: flex;
  align-items: center;

  &--variant {
    &-transparent {
      @include action-effect-button(transparent);

      color: var(--color-foreground-inverted);
    }

    &-light {
      @include action-effect-button(var(--color-surface-bg));

      color: var(--color-foreground-regular);
    }

    &-primary {
      @include action-effect-button(var(--color-surface-primary));

      color: var(--color-foreground-light);

      &.base-tag--outlined {
        background-color: rgb(0 0 0 / 0%);
        border: 1px solid var(--color-surface-primary);
        color: var(--color-foreground-primary);
      }
    }

    &-dark {
      @include action-effect-button(rgb(51 51 51 / 1));

      color: var(--color-foreground-light);
    }

    &-secondary {
      @include action-effect-button(var(--color-surface-placeholder));

      color: var(--color-foreground-regular);
    }
  }

  &--custom-planner-color {
    background-color: var(--tag-planner-bg);
    color: var(--color-foreground-light);
    border: none;

    &.base-tag--size-mini {
      padding-left: rem(6px);
      padding-right: rem(6px);
    }
  }

  &--size {
    &-mini {
      border-radius: rem(4px);
      gap: var(--tag-gap, rem(2px));
      padding-left: rem(4px);
      padding-right: rem(4px);
      height: 20px;
      font-size: rem(10px);
      line-height: 100%;
      font-weight: 600;

      &.base-tag--rounded {
        border-radius: rem(20px);
      }
    }

    &-md {
      border-radius: rem(8px);
      gap: var(--tag-gap, rem(4px));
      padding-left: rem(12px);
      padding-right: rem(12px);
      height: 30px;
      font-size: rem(14px);
      line-height: 100%;
      font-weight: 500;

      &.base-tag--rounded {
        border-radius: rem(30px);
      }
    }
  }
}
</style>
